import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, Typography, Alert, TextField, MenuItem, Stack, Chip, Link, IconButton,
  Card, CardActionArea, CardContent, Divider, Paper, Checkbox, Accordion, AccordionSummary, AccordionDetails,
  Menu, ListItemIcon, ListItemText, CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import PauseCircleOutlineRounded from '@mui/icons-material/PauseCircleOutlineRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import PowerSettingsNewRounded from '@mui/icons-material/PowerSettingsNewRounded';
import CloseIcon from '@mui/icons-material/Close';
import SortRounded from '@mui/icons-material/SortRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import ApiRounded from '@mui/icons-material/ApiRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import VpnKeyRounded from '@mui/icons-material/VpnKeyRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';
import NotificationsActiveRounded from '@mui/icons-material/NotificationsActiveRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import InputAdornment from '@mui/material/InputAdornment';
// Avoid Grid to reduce typings complexity in modal; simple Box layout instead
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../config';
import { buildLoginUrl } from '../utils/loginRedirect';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { trackConnectorAdded } from '../utils/analytics';
import { useClaimGate } from './onboarding/claim/ClaimGateContext';
import { markProductStep } from '../hooks/useProductOnboarding';
import { useProductOnboarding } from '../hooks/useProductOnboarding';
import { ReactComponent as GoogleDriveSvg } from '../assets/connectors/google-drive.svg';
import { ReactComponent as PostgresSvg } from '../assets/connectors/postgres.svg';
import { ReactComponent as MySQLSvg } from '../assets/connectors/mysql.svg';
import { ReactComponent as OneDriveSvg } from '../assets/connectors/onedrive.svg';
import { ReactComponent as SharePointSvg } from '../assets/connectors/sharepoint.svg';
import { ReactComponent as GoogleAdsSvg } from '../assets/connectors/google-ads.svg';
import { ReactComponent as BigQuerySvg } from '../assets/connectors/bigquery.svg';
import { ReactComponent as SnowflakeSvg } from '../assets/connectors/Snowflake.svg';
import { ReactComponent as RedshiftSvg } from '../assets/connectors/redshift.svg';
import { ReactComponent as ShopifySvg } from '../assets/connectors/shopify.svg';
import { ReactComponent as HubSpotSvg } from '../assets/connectors/hubspot.svg';
import { ReactComponent as MCPSvg } from '../assets/connectors/mcp.svg';
import { ReactComponent as AirtableSvg } from '../assets/connectors/airtable.svg';
import { ReactComponent as AgencyZoomSvg } from '../assets/connectors/agencyzoom.svg';
import { ReactComponent as SandboxAmsSvg } from '../assets/connectors/sandbox-ams.svg';
import ezlynxLogo from '../assets/connectors/ezlynx.png';
import ams360Logo from '../assets/connectors/ams360.png';
import vertaforeLogo from '../assets/connectors/vertafore.png';
import agencyblocLogo from '../assets/connectors/agencybloc.webp';
import ipipelineLogo from '../assets/connectors/ipipeline.png';
import zywaveLogo from '../assets/connectors/zywave.png';
import zinniaLogo from '../assets/connectors/zinnia.svg';
import ringcentralLogo from '../assets/connectors/ringcentral.png';
import five9Logo from '../assets/connectors/five9.svg';
import salesforceLogo from '../assets/connectors/salesforce.svg';
import zohoLogo from '../assets/connectors/zoho.svg';
import pipedriveLogo from '../assets/connectors/pipedrive.png';
import regalLogo from '../assets/connectors/regal.png';
import aircallLogo from '../assets/connectors/aircall.svg';
import dialpadLogo from '../assets/connectors/dialpad.png';
import genesysLogo from '../assets/connectors/genesys.png';
import outlookLogo from '../assets/connectors/outlook.svg';
import gmailLogo from '../assets/connectors/gmail.svg';
import twilioLogo from '../assets/connectors/twilio.svg';
import Tooltip from '@mui/material/Tooltip';
import { formatRelativeTime } from '../utils/date';
import { SyncJobContext } from '../ui/SyncJobProvider';
import OnboardingCoachmark from '../components/OnboardingCoachmark';
import { AppDialog, ConfirmDialog } from '../components/dialog';

const API_URL = buildApiUrl('');

// ── Status / category helpers ──────────────────────────────────────────────
type ConnectorStatusKey = 'active' | 'inactive' | 'failed' | 'pending';

function normalizeStatus(raw: string | undefined | null): ConnectorStatusKey {
  if (raw === 'active' || raw === 'inactive' || raw === 'failed') return raw;
  return 'pending';
}

const STATUS_META: Record<ConnectorStatusKey, { label: string; tone: 'success' | 'neutral' | 'error' | 'warning' }> = {
  active: { label: 'Healthy', tone: 'success' },
  inactive: { label: 'Paused', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'error' },
  pending: { label: 'Pending', tone: 'warning' },
};

type CategoryKey = 'database' | 'files' | 'api' | 'other';
const CATEGORY_META: Record<CategoryKey, { label: string; icon: any }> = {
  database: { label: 'Databases', icon: StorageRounded },
  files: { label: 'Files & storage', icon: FolderRounded },
  api: { label: 'APIs & SaaS', icon: ApiRounded },
  other: { label: 'Other', icon: ApiRounded },
};

const TYPE_TO_CATEGORY: Record<string, CategoryKey> = {
  postgres: 'database',
  mysql: 'database',
  snowflake: 'database',
  redshift: 'database',
  bigquery: 'database',
  google_drive: 'files',
  onedrive: 'files',
  sharepoint: 'files',
  shopify: 'api',
  hubspot: 'api',
  airtable: 'api',
  agencyzoom: 'api',
  sandbox_ams: 'api',
  google_ads: 'api',
  mcp: 'api',
};

function categorizeConnector(c: { type: string; category?: string }): CategoryKey {
  // Prefer the explicit hand-curated map (frontend-stable) over backend-stored
  // category, which historically used different vocab (sql/files/rest).
  return TYPE_TO_CATEGORY[c.type] || 'other';
}

const TYPE_LABELS: Record<string, string> = {
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  snowflake: 'Snowflake',
  redshift: 'AWS Redshift',
  bigquery: 'BigQuery',
  google_drive: 'Google Drive',
  onedrive: 'OneDrive',
  sharepoint: 'SharePoint',
  shopify: 'Shopify',
  hubspot: 'HubSpot CRM',
  airtable: 'Airtable',
  agencyzoom: 'AgencyZoom',
  sandbox_ams: 'Sandbox AMS',
  google_ads: 'Google Ads',
  mcp: 'MCP',
};

function prettyType(t: string): string {
  return TYPE_LABELS[t] || t.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

// Reusable token-driven status pill (dot + label).
function StatusPill({ status }: { status: ConnectorStatusKey }) {
  const meta = STATUS_META[status];
  return (
    <Box
      sx={(t: any) => {
        const palette = {
          success: { bg: t.tokens.semantic.successSoft, fg: t.tokens.semantic.success },
          neutral: { bg: t.tokens.surface.sunken, fg: t.tokens.text.secondary },
          error: { bg: t.tokens.semantic.dangerSoft, fg: t.tokens.semantic.danger },
          warning: { bg: t.tokens.semantic.warningSoft, fg: t.tokens.semantic.warning },
        }[meta.tone];
        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.625,
          height: 22,
          px: 1,
          borderRadius: `${t.ds.radius.full}px`,
          bgcolor: palette.bg,
          color: palette.fg,
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: 1,
        };
      }}
    >
      <Box sx={(t: any) => {
        const dot = {
          success: t.tokens.semantic.success,
          neutral: t.tokens.border.strong,
          error: t.tokens.semantic.danger,
          warning: t.tokens.semantic.warning,
        }[meta.tone];
        return { width: 6, height: 6, borderRadius: '50%', bgcolor: dot, flexShrink: 0 };
      }} />
      {meta.label}
    </Box>
  );
}

// ── Health card — 1 of 4 in the top strip. Click toggles a status filter. ─
function HealthCard({
  tone,
  icon,
  count,
  label,
  hint,
  onClick,
  active,
  emphasize,
  valueIsText,
}: {
  tone: 'success' | 'neutral' | 'error' | 'info';
  icon: React.ReactNode;
  count: number | string;
  label: string;
  hint?: string;
  onClick?: () => void;
  active?: boolean;
  emphasize?: boolean;
  valueIsText?: boolean;
}) {
  const isClickable = Boolean(onClick);
  return (
    <Box
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); } : undefined}
      sx={(t) => {
        const emphasizeBorder = emphasize ? `1px solid ${t.tokens.semantic.danger}` : undefined;
        return {
          p: 1.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          borderRadius: `${t.ds.radius.md}px`,
          bgcolor: t.tokens.surface.raised,
          border: emphasizeBorder || `1px solid ${active ? t.tokens.brand.primary : t.tokens.border.subtle}`,
          cursor: isClickable ? 'pointer' : 'default',
          transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          '&:hover': isClickable ? {
            borderColor: emphasize ? t.tokens.semantic.danger : t.tokens.border.default,
            bgcolor: t.tokens.surface.sunken,
          } : {},
          '&:focus-visible': { outline: `2px solid ${t.tokens.brand.primary}`, outlineOffset: 2 },
        };
      }}
    >
      {/* Top row — small icon + uppercase label form the "category" badge. */}
      <Stack direction="row" alignItems="center" spacing={0.875} sx={{ minWidth: 0 }}>
        <Box
          sx={(t) => {
            const palettes = {
              success: { fg: t.tokens.semantic.success },
              neutral: { fg: t.tokens.text.tertiary },
              error:   { fg: t.tokens.semantic.danger },
              info:    { fg: t.tokens.brand.primary },
            } as const;
            return {
              color: palettes[tone].fg,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& > *': { fontSize: '16px !important' },
            };
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={(t) => ({
            fontSize: '0.6875rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: t.tokens.text.tertiary,
            lineHeight: 1,
          })}
          noWrap
        >
          {label}
        </Typography>
      </Stack>

      {/* Hero metric — same size class for both numeric and text values to keep
          all 4 cards visually consistent. */}
      <Typography
        sx={(t) => ({
          fontSize: valueIsText ? '0.9375rem' : '1.5rem',
          lineHeight: 1.1,
          fontWeight: 700,
          letterSpacing: valueIsText ? 0 : '-0.01em',
          color: emphasize ? t.tokens.semantic.danger : t.tokens.text.primary,
        })}
        noWrap
        title={typeof count === 'string' ? count : undefined}
      >
        {count}
      </Typography>

      {hint && (
        <Typography
          sx={(t) => ({ fontSize: '0.6875rem', color: t.tokens.text.tertiary, lineHeight: 1.3 })}
          noWrap
        >
          {hint}
        </Typography>
      )}
    </Box>
  );
}

// ── Filter chip — value + count in a single rounded pill. ────────────────
function FilterChip({
  label, count, active, onClick, tone,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone?: 'default' | 'error';
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      sx={(t) => {
        const errorTone = tone === 'error' && count > 0;
        return {
          height: 28,
          px: 1.25,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.625,
          borderRadius: `${t.ds.radius.full}px`,
          fontSize: '0.75rem',
          fontWeight: 600,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          color: active ? t.tokens.brand.onPrimary : (errorTone ? t.tokens.semantic.danger : t.tokens.text.secondary),
          bgcolor: active ? t.tokens.brand.primary : (errorTone ? t.tokens.semantic.dangerSoft : 'transparent'),
          border: `1px solid ${active ? t.tokens.brand.primary : (errorTone ? 'transparent' : t.tokens.border.default)}`,
          transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          '&:hover': active ? {} : { borderColor: t.tokens.border.strong, bgcolor: t.tokens.surface.sunken },
          '&:focus-visible': { outline: `2px solid ${t.tokens.brand.primary}`, outlineOffset: 2 },
        };
      }}
    >
      {label}
      <Box
        component="span"
        sx={(t) => ({
          fontSize: '0.6875rem',
          fontWeight: 700,
          opacity: active ? 0.85 : 0.7,
          color: 'inherit',
        })}
      >
        {count}
      </Box>
    </Box>
  );
}

// ── Empty-state hero — shown when there are no connectors yet. ────────────
function EmptyConnectorsHero({
  onPickType,
  onSeeAll,
  connectorTypes,
}: {
  onPickType: (typeValue: string) => void;
  onSeeAll: () => void;
  connectorTypes: any[];
}) {
  // Recommended starter set — uses the actual labels/icons from the live
  // connector type list so we never drift.
  const RECOMMENDED = ['postgres', 'bigquery', 'snowflake', 'google_drive', 'airtable', 'hubspot'];
  const tiles = RECOMMENDED
    .map((v) => connectorTypes.find((ct: any) => ct.value === v))
    .filter(Boolean);

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        p: { xs: 3, md: 5 },
        textAlign: 'center',
        borderRadius: `${t.ds.radius.lg}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, mb: 1 }}>
        Connect your first data source
      </Typography>
      <Typography
        variant="body2"
        sx={(t) => ({ color: t.tokens.text.secondary, fontSize: '0.875rem', maxWidth: 520, mx: 'auto', mb: 3 })}
      >
        Pull in your databases, files, and APIs so Klaris can answer questions across all of your data.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 1.25,
          maxWidth: 720,
          mx: 'auto',
        }}
      >
        {tiles.map((ct: any) => {
          const Icon = ct.icon;
          return (
            <Card
              key={ct.value}
              variant="outlined"
              sx={(t) => ({
                borderRadius: `${t.ds.radius.md}px`,
                borderColor: t.tokens.border.subtle,
                textAlign: 'left',
                transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: t.tokens.shadow.md,
                  borderColor: t.tokens.brand.primary,
                },
              })}
            >
              <CardActionArea onClick={() => onPickType(ct.value)} sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={(t) => ({
                      width: 36,
                      height: 36,
                      bgcolor: t.tokens.brand.primarySoft,
                      borderRadius: `${t.ds.radius.sm}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      border: `1px solid ${t.tokens.border.subtle}`,
                    })}
                  >
                    {Icon && <Icon style={{ width: 18, height: 18 }} />}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }} noWrap>
                      {ct.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={(t) => ({ fontSize: '0.75rem', color: t.tokens.text.secondary, lineHeight: 1.35 })}
                      noWrap
                    >
                      {ct.description}
                    </Typography>
                  </Box>
                </Stack>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      <Button
        variant="text"
        size="small"
        endIcon={<ChevronRightRounded sx={{ fontSize: 16 }} />}
        onClick={onSeeAll}
        sx={{ mt: 2.5, textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
      >
        See all connectors
      </Button>
    </Paper>
  );
}

// ── Add Connector dialog — grouped, searchable, keyboard-navigable picker.
// Single source of truth for choosing which connector to add.
function AddConnectorDialog({
  open,
  onClose,
  connectorTypes,
  comingSoon,
  existingTypes,
  search,
  onSearchChange,
  highlight,
  setHighlight,
  onPick,
  notifySet,
  onToggleNotify,
}: {
  open: boolean;
  onClose: () => void;
  connectorTypes: any[];
  comingSoon: { value: string; label: string; description: string; category: CategoryKey; keywords: string[] }[];
  existingTypes: Set<string>;
  search: string;
  onSearchChange: (v: string) => void;
  highlight: number;
  setHighlight: (n: number) => void;
  onPick: (typeValue: string) => void;
  notifySet: Set<string>;
  onToggleNotify: (value: string) => void;
}) {
  const q = search.trim().toLowerCase();

  // Match function — searches across label, description, keywords, value.
  const matches = (item: { label: string; description?: string; keywords?: string[]; value?: string }) => {
    if (!q) return true;
    const haystack = [
      item.label,
      item.description || '',
      ...(item.keywords || []),
      item.value || '',
    ].join(' ').toLowerCase();
    return haystack.includes(q);
  };

  const filteredEnabled = useMemo(
    () => connectorTypes.filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectorTypes, q],
  );
  const filteredComingSoon = useMemo(
    () => comingSoon.filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comingSoon, q],
  );

  // Suggested = the agency systems: AgencyZoom and HubSpot, which work today,
  // then the insurance systems that are coming, greyed out. When searching,
  // suggested is hidden so it doesn't compete with results.
  const insuranceSoon = useMemo(
    () => comingSoon.filter((c) => INSURANCE_COMING_SOON.some((i) => i.value === c.value)),
    [comingSoon],
  );
  const suggested = useMemo(() => {
    if (q) return [];
    const ready = SUGGESTED_FIRST
      .map((v) => connectorTypes.find((c: any) => c.value === v))
      .filter(Boolean);
    return [...ready, ...insuranceSoon.map((c) => ({ ...c, soon: true }))];
  }, [connectorTypes, insuranceSoon, q]);

  // Build ordered groups for rendering.
  type Group = { id: string; label: string; icon: any; items: any[]; isComingSoon?: boolean };
  const groups: Group[] = useMemo(() => {
    const out: Group[] = [];
    if (suggested.length > 0) {
      out.push({ id: 'suggested', label: 'Suggested for you', icon: StarRounded, items: suggested });
    }
    const byCategory: Record<CategoryKey, any[]> = { database: [], files: [], api: [], other: [] };
    for (const ct of filteredEnabled) {
      const cat = (ct.category as CategoryKey) || 'other';
      byCategory[cat].push(ct);
    }
    if (byCategory.database.length) {
      out.push({ id: 'database', label: 'Databases & warehouses', icon: StorageRounded, items: byCategory.database });
    }
    if (byCategory.files.length) {
      out.push({ id: 'files', label: 'Files & storage', icon: FolderRounded, items: byCategory.files });
    }
    if (byCategory.api.length) {
      out.push({ id: 'api', label: 'APIs & SaaS', icon: ApiRounded, items: byCategory.api });
    }
    if (byCategory.other.length) {
      out.push({ id: 'other', label: 'Other', icon: ApiRounded, items: byCategory.other });
    }
    // The insurance systems already sit under Suggested when it shows.
    const later = q ? filteredComingSoon : filteredComingSoon.filter((c) => !insuranceSoon.includes(c));
    if (later.length) {
      out.push({ id: 'others', label: 'Others', icon: ScheduleRounded, items: later, isComingSoon: true });
    }
    return out;
  }, [suggested, filteredEnabled, filteredComingSoon, insuranceSoon, q]);

  // Flat list of selectable items (for keyboard nav). Coming-soon excluded.
  const flatItems = useMemo(() => {
    const out: any[] = [];
    for (const g of groups) {
      if (g.isComingSoon) continue;
      for (const it of g.items) if (!it.soon) out.push(it);
    }
    return out;
  }, [groups]);

  // Reset highlight when search changes
  React.useEffect(() => {
    setHighlight(flatItems.length > 0 ? 0 : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flatItems.length === 0) return;
      setHighlight((highlight + 1) % flatItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flatItems.length === 0) return;
      setHighlight((highlight - 1 + flatItems.length) % flatItems.length);
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && highlight < flatItems.length) {
        e.preventDefault();
        onPick(flatItems[highlight].value);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title="Add a connector"
      subtitle="Type to search · ↑↓ to navigate · ↵ to select · Esc to close"
      paperSx={{ minHeight: '65vh' }}
    >
      <Box sx={{ pb: 0 }}>
        <TextField
          placeholder="Search by name, type, or use case (e.g. ‘warehouse’, ‘crm’, ‘sql’)"
          size="small"
          fullWidth
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={(t: any) => ({ fontSize: 18, color: t.tokens.text.tertiary })} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ mb: 1.75, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        />

        <Divider sx={{ mb: 2 }} />
      </Box>

        {/* Empty state — no matches at all. */}
        {groups.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, mb: 0.5 }}>
              No connectors match “{search}”
            </Typography>
            <Typography variant="body2" sx={(t: any) => ({ color: t.tokens.text.secondary, mb: 2 })}>
              Don't see what you need? We're always adding new sources.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<NotificationsActiveRounded sx={{ fontSize: 16 }} />}
              onClick={() => onToggleNotify(`request:${search}`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {notifySet.has(`request:${search}`) ? 'Request submitted' : `Request “${search}”`}
            </Button>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {(() => {
              let runningIndex = 0;
              return groups.map((group) => {
                const items = group.items;
                const startIndex = group.isComingSoon ? -1 : runningIndex;
                // Greyed-out tiles inside a working group come last and are never highlighted.
                if (!group.isComingSoon) runningIndex += items.filter((it: any) => !it.soon).length;
                const GroupIcon = group.icon;
                return (
                  <Box key={group.id}>
                    {/* Section header */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <GroupIcon sx={(t: any) => ({ fontSize: 14, color: t.tokens.text.tertiary })} />
                      <Typography
                        sx={(t: any) => ({
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: t.tokens.text.tertiary,
                        })}
                      >
                        {group.label}
                      </Typography>
                      <Typography sx={(t: any) => ({ fontSize: '0.75rem', color: t.tokens.text.tertiary })}>
                        · {items.length}
                      </Typography>
                    </Stack>

                    {/* Cards grid */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 1.25,
                        minWidth: 0,
                      }}
                    >
                      {items.map((item: any, i: number) => {
                        const soon = Boolean(group.isComingSoon || item.soon);
                        return (
                          <ConnectorPickerCard
                            key={item.value}
                            item={item}
                            isComingSoon={soon}
                            query={q}
                            isHighlighted={!soon && startIndex + i === highlight}
                            alreadyAdded={existingTypes.has(item.value)}
                            onClick={() => { if (!soon) onPick(item.value); }}
                            onMouseEnter={() => {
                              if (!soon) setHighlight(startIndex + i);
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              });
            })()}
          </Stack>
        )}
    </AppDialog>
  );
}

// ── Single card in the picker. Drops the bottom auth pill in favour of a
// right-aligned auth icon and an optional "In workspace" badge. ──────────
function ConnectorPickerCard({
  item,
  isComingSoon,
  query,
  isHighlighted,
  alreadyAdded,
  onClick,
  onMouseEnter,
}: {
  item: any;
  isComingSoon: boolean;
  query: string;
  isHighlighted: boolean;
  alreadyAdded: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  const Icon = isComingSoon ? COMING_SOON_ICONS[item.value] : item.icon;
  const isOAuth = item.requires_oauth;
  return (
    <Card
      variant="outlined"
      onMouseEnter={onMouseEnter}
      sx={(t) => ({
        borderRadius: `${t.ds.radius.md}px`,
        borderColor: isHighlighted ? t.tokens.brand.primary : t.tokens.border.subtle,
        boxShadow: isHighlighted ? t.tokens.shadow.sm : 'none',
        bgcolor: isHighlighted ? t.tokens.brand.primarySofter : t.tokens.surface.raised,
        // Not available yet: shown, greyed out, and not clickable.
        opacity: isComingSoon ? 0.5 : 1,
        filter: isComingSoon ? 'grayscale(1)' : 'none',
        overflow: 'visible',
        position: 'relative',
        transition: `all ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        '&:hover': {
          transform: isComingSoon ? 'none' : 'translateY(-1px)',
          boxShadow: t.tokens.shadow.md,
          borderColor: isComingSoon ? t.tokens.border.default : t.tokens.brand.primary,
        },
      })}
    >
      <CardActionArea onClick={onClick} disabled={isComingSoon} aria-disabled={isComingSoon}
        sx={{ alignItems: 'stretch', height: '100%' }}>
        <CardContent sx={{ py: 1.5, px: 1.75, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={(t) => ({
                width: 40,
                height: 40,
                bgcolor: isComingSoon ? t.tokens.surface.sunken : t.tokens.brand.primarySoft,
                borderRadius: `${t.ds.radius.sm}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: `1px solid ${t.tokens.border.subtle}`,
              })}
            >
              {Icon && <Icon style={{ height: 20, width: 20 }} />}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.625} sx={{ mb: 0.25 }}>
                <Typography
                  sx={{ fontSize: '0.875rem', fontWeight: 700, lineHeight: 1.3, minWidth: 0 }}
                  noWrap
                >
                  {highlightMatch(item.label, query)}
                </Typography>
                {alreadyAdded && (
                  <Tooltip title="Already added in your workspace">
                    <CheckCircleRounded
                      sx={(t: any) => ({ fontSize: 13, color: t.tokens.semantic.success, flexShrink: 0 })}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Typography
                variant="body2"
                sx={(t: any) => ({
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  color: t.tokens.text.secondary,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                })}
              >
                {item.description}
              </Typography>
            </Box>
            {/* Right-side affordance */}
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {isComingSoon ? null : (
                <Tooltip title={isOAuth ? 'Connects with OAuth (1 click)' : 'Connects with credentials'}>
                  <Box
                    sx={(t: any) => ({
                      width: 24,
                      height: 24,
                      borderRadius: `${t.ds.radius.sm}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: t.tokens.text.tertiary,
                    })}
                  >
                    {isOAuth ? <LinkRounded sx={{ fontSize: 16 }} /> : <VpnKeyRounded sx={{ fontSize: 14 }} />}
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ── Connector row — rich, two-line, with hover quick actions. ─────────────
function ConnectorRow({
  connector,
  isLast,
  isMobile,
  isSyncing,
  onOpen,
  onMenu,
}: {
  connector: Connector;
  isLast: boolean;
  isMobile: boolean;
  isSyncing: boolean;
  onOpen: () => void;
  onMenu: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  const Icon = connectorIcons[connector.type];
  const status = normalizeStatus(connector.status);
  const isFailed = status === 'failed';
  const lastSyncLabel = connector.last_schema_fetch
    ? `Last sync ${formatRelativeTime(connector.last_schema_fetch)}`
    : '';
  const createdLabel = connector.created_at ? `Added ${formatRelativeTime(connector.created_at)}` : '';

  return (
    <Box
      onClick={onOpen}
      sx={(t) => ({
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: { xs: 1.5, md: 2.5 },
        py: { xs: 1.25, md: 1.5 },
        cursor: 'pointer',
        borderBottom: isLast ? 'none' : `1px solid ${t.tokens.border.subtle}`,
        borderLeft: isFailed ? `3px solid ${t.tokens.semantic.danger}` : '3px solid transparent',
        transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        '&:hover': { bgcolor: t.tokens.brand.primarySofter },
      })}
    >
      {/* Icon tile */}
      <Box
        sx={(t) => ({
          width: 40,
          height: 40,
          bgcolor: t.tokens.brand.primarySoft,
          borderRadius: `${t.ds.radius.sm}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: `1px solid ${t.tokens.border.subtle}`,
        })}
      >
        {Icon && <Icon style={{ width: 20, height: 20 }} />}
      </Box>

      {/* Body — two lines */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Typography
            sx={(t) => ({
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: t.tokens.text.primary,
              lineHeight: 1.25,
            })}
            noWrap
          >
            {connector.name || `${prettyType(connector.type)} connector`}
          </Typography>
          <StatusPill status={status} />
          {isSyncing && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CircularProgress size={12} thickness={5} />
              <Typography
                sx={(t) => ({ fontSize: '0.6875rem', fontWeight: 600, color: t.tokens.text.tertiary })}
              >
                Syncing…
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Failed: surface the error inline (Tier 1 ask). Otherwise meta line. */}
        {isFailed && connector.error_message ? (
          <Typography
            sx={(t) => ({
              mt: 0.5,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: t.tokens.semantic.danger,
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
            title={connector.error_message}
          >
            {connector.error_message}
          </Typography>
        ) : (
          <Typography
            sx={(t) => ({
              mt: 0.25,
              fontSize: '0.75rem',
              color: t.tokens.text.tertiary,
              lineHeight: 1.4,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexWrap: 'wrap',
            })}
          >
            <span>{prettyType(connector.type)}</span>
            {lastSyncLabel && (
              <>
                <Box component="span" sx={(t) => ({ width: 3, height: 3, borderRadius: '50%', bgcolor: t.tokens.text.tertiary, opacity: 0.6 })} />
                <span>{lastSyncLabel}</span>
              </>
            )}
            {createdLabel && (
              <>
                <Box component="span" sx={(t) => ({ width: 3, height: 3, borderRadius: '50%', bgcolor: t.tokens.text.tertiary, opacity: 0.6 })} />
                <span>{createdLabel}</span>
              </>
            )}
          </Typography>
        )}
      </Box>

      {/* Right side — Fix CTA on failed; quick actions on hover; kebab. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {isFailed && (
          <Tooltip title="Open connector to fix the issue">
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
              sx={(t) => ({
                py: 0.25,
                px: 1.25,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: `${t.ds.radius.sm}px`,
                color: t.tokens.semantic.danger,
                borderColor: t.tokens.semantic.danger,
                '&:hover': {
                  borderColor: t.tokens.semantic.danger,
                  bgcolor: t.tokens.semantic.dangerSoft,
                },
              })}
            >
              Fix
            </Button>
          </Tooltip>
        )}

        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onMenu(e); }}
          sx={(t) => ({ color: t.tokens.text.secondary })}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

// Icon mapping for connector types
const connectorIcons: { [key: string]: any } = {
  'postgres': PostgresSvg,
  'mysql': MySQLSvg,
  'google_drive': GoogleDriveSvg,
  'snowflake': SnowflakeSvg,
  'redshift': RedshiftSvg,
  'onedrive': OneDriveSvg,
  'sharepoint': SharePointSvg,
  'google_ads': GoogleAdsSvg,
  'bigquery': BigQuerySvg,
  'shopify': ShopifySvg,
  'hubspot': HubSpotSvg,
  'mcp': MCPSvg,
  'airtable': AirtableSvg,
  'agencyzoom': AgencyZoomSvg,
  'sandbox_ams': SandboxAmsSvg,
};

type Connector = {
  connector_id: string;
  name?: string;
  type: string;
  category?: string;  // Added: connector category (sql, files, rest)
  status: string;
  created_at?: string;
  last_schema_fetch?: string;
  error_message?: string;
};

// ── Coming-soon catalog (placeholder for future connectors). Click to upvote
// — count is persisted in localStorage so we can surface demand later. ──
type ComingSoon = { value: string; label: string; description: string; category: CategoryKey; keywords: string[] };

/** The agency systems that work today, first under "Suggested for you". */
const SUGGESTED_FIRST = ['agencyzoom', 'hubspot'];

// Insurance agency systems, shown greyed out at the top of the picker under
// "Suggested for you", beside the two that work today (AgencyZoom, HubSpot).
// Most are from the website's integrations list (website repo, index.html).
const INSURANCE_COMING_SOON: ComingSoon[] = [
  { value: 'ezlynx', label: 'EZLynx', description: 'Agency management and comparative rating', category: 'api', keywords: ['insurance', 'agency', 'ams', 'rating', 'quotes', 'carriers'] },
  { value: 'ams360', label: 'AMS360', description: 'Vertafore agency management system', category: 'api', keywords: ['insurance', 'agency', 'ams', 'vertafore', 'policies'] },
  { value: 'vertafore', label: 'Vertafore', description: 'Agency management, rating and carrier connections', category: 'api', keywords: ['insurance', 'agency', 'ams', 'sagitta', 'qqcatalyst'] },
  { value: 'agencybloc', label: 'AgencyBloc', description: 'CRM and agency management for life and health', category: 'api', keywords: ['insurance', 'life', 'health', 'benefits', 'crm', 'agency'] },
  { value: 'applied_epic', label: 'Applied Epic', description: 'Agency management system', category: 'api', keywords: ['insurance', 'agency', 'ams', 'applied'] },
  { value: 'hawksoft', label: 'HawkSoft', description: 'Agency management system', category: 'api', keywords: ['insurance', 'agency', 'ams'] },
  { value: 'ipipeline', label: 'iPipeline', description: 'Life insurance applications and e-signature', category: 'api', keywords: ['insurance', 'life', 'applications', 'e-app'] },
  { value: 'zywave', label: 'Zywave', description: 'Agency sales, service and content', category: 'api', keywords: ['insurance', 'agency', 'benefits', 'content'] },
];

const COMING_SOON_CONNECTORS: ComingSoon[] = [
  ...INSURANCE_COMING_SOON,
  { value: 'salesforce', label: 'Salesforce', description: 'CRM: leads, contacts and opportunities', category: 'api', keywords: ['crm', 'leads', 'contacts', 'sales'] },
  { value: 'dynamics365', label: 'Microsoft Dynamics 365', description: 'CRM: leads, contacts and accounts', category: 'api', keywords: ['crm', 'microsoft', 'leads'] },
  { value: 'zoho', label: 'Zoho CRM', description: 'CRM: leads, contacts and deals', category: 'api', keywords: ['crm', 'leads', 'contacts'] },
  { value: 'pipedrive', label: 'Pipedrive', description: 'CRM: leads and deals', category: 'api', keywords: ['crm', 'deals', 'sales'] },
  { value: 'outlook', label: 'Microsoft Outlook', description: 'Email and calendar', category: 'api', keywords: ['email', 'calendar', 'microsoft', 'inbox'] },
  { value: 'gmail', label: 'Gmail', description: 'Email', category: 'api', keywords: ['email', 'google', 'inbox'] },
  { value: 'twilio', label: 'Twilio', description: 'Phone calls, text messages and WhatsApp', category: 'api', keywords: ['phone', 'sms', 'whatsapp', 'calls', 'voice'] },
  { value: 'ringcentral', label: 'RingCentral', description: 'Business phone system and contact center', category: 'api', keywords: ['phone', 'calls', 'dialer', 'voip'] },
  { value: 'five9', label: 'Five9', description: 'Contact center and dialer', category: 'api', keywords: ['phone', 'calls', 'dialer', 'contact center'] },
  { value: 'nice_cxone', label: 'NICE CXone', description: 'Contact center and dialer', category: 'api', keywords: ['phone', 'calls', 'dialer', 'contact center'] },
  { value: 'regal', label: 'Regal', description: 'Outbound calling and texting for sales teams', category: 'api', keywords: ['phone', 'calls', 'dialer', 'sms'] },
  { value: 'aircall', label: 'Aircall', description: 'Business phone system', category: 'api', keywords: ['phone', 'calls', 'voip'] },
  { value: 'dialpad', label: 'Dialpad', description: 'Business phone system', category: 'api', keywords: ['phone', 'calls', 'voip'] },
  { value: 'genesys', label: 'Genesys', description: 'Contact center', category: 'api', keywords: ['phone', 'calls', 'contact center'] },
  { value: 'zinnia', label: 'Zinnia', description: 'Life and annuity policy administration', category: 'api', keywords: ['insurance', 'life', 'annuity', 'policies'] },
  { value: 'surelc', label: 'SureLC', description: 'Agent contracting and licensing', category: 'api', keywords: ['insurance', 'contracting', 'licensing', 'agents'] },
  { value: 'winflex', label: 'WinFlex', description: 'Life insurance illustrations and quotes', category: 'api', keywords: ['insurance', 'life', 'quotes', 'illustrations'] },
  { value: 'databricks', label: 'Databricks', description: 'Data and AI platform', category: 'database', keywords: ['warehouse', 'lakehouse', 'sql', 'spark'] },
  { value: 'onedrive', label: 'Microsoft OneDrive', description: 'Cloud storage for files and documents', category: 'files', keywords: ['microsoft', 'storage', 'documents'] },
  { value: 'sharepoint', label: 'SharePoint', description: 'Collaborative document management', category: 'files', keywords: ['microsoft', 'documents', 'collaboration'] },
  { value: 'google_ads', label: 'Google Ads', description: 'Advertising campaign data and analytics', category: 'api', keywords: ['advertising', 'marketing', 'campaigns', 'ads'] },
  { value: 'mcp', label: 'MCP', description: 'Model Context Protocol servers', category: 'api', keywords: ['model', 'protocol', 'context'] },
];

// Icon resolver used in the coming-soon section so we don't duplicate the map.
/** A logo kept as an image file (PNG, WebP or a plain SVG), drawn like the SVG icons. */
const logoImage = (src: string, name: string) => function LogoImage({ style }: { style?: React.CSSProperties }) {
  return <img src={src} alt={name} style={{ ...style, objectFit: 'contain' }} />;
};

const COMING_SOON_ICONS: Record<string, any> = {
  ezlynx: logoImage(ezlynxLogo, 'EZLynx'),
  ams360: logoImage(ams360Logo, 'AMS360'),
  vertafore: logoImage(vertaforeLogo, 'Vertafore'),
  agencybloc: logoImage(agencyblocLogo, 'AgencyBloc'),
  applied_epic: ApiRounded,
  hawksoft: ApiRounded,
  ipipeline: logoImage(ipipelineLogo, 'iPipeline'),
  zywave: logoImage(zywaveLogo, 'Zywave'),
  salesforce: logoImage(salesforceLogo, 'Salesforce'),
  dynamics365: ApiRounded,
  zoho: logoImage(zohoLogo, 'Zoho CRM'),
  pipedrive: logoImage(pipedriveLogo, 'Pipedrive'),
  outlook: logoImage(outlookLogo, 'Outlook'),
  gmail: logoImage(gmailLogo, 'Gmail'),
  twilio: logoImage(twilioLogo, 'Twilio'),
  ringcentral: logoImage(ringcentralLogo, 'RingCentral'),
  five9: logoImage(five9Logo, 'Five9'),
  nice_cxone: ApiRounded,
  regal: logoImage(regalLogo, 'Regal'),
  aircall: logoImage(aircallLogo, 'Aircall'),
  dialpad: logoImage(dialpadLogo, 'Dialpad'),
  genesys: logoImage(genesysLogo, 'Genesys'),
  zinnia: logoImage(zinniaLogo, 'Zinnia'),
  surelc: ApiRounded,
  winflex: ApiRounded,
  databricks: StorageRounded,
  onedrive: OneDriveSvg,
  sharepoint: SharePointSvg,
  google_ads: GoogleAdsSvg,
  mcp: MCPSvg,
};

// ── Highlight matched substring in a label, for command-palette feel. ──
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const i = text.toLowerCase().indexOf(query.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <Box component="span" sx={(t: any) => ({ color: t.tokens.brand.primary, fontWeight: 700 })}>
        {text.slice(i, i + query.length)}
      </Box>
      {text.slice(i + query.length)}
    </>
  );
}

// ── Notify-me hook: persist user upvotes for coming-soon connectors. ──
const NOTIFY_STORAGE_KEY = 'klaris_connector_notify';
function readNotifySet(): Set<string> {
  try {
    const raw = window.localStorage.getItem(NOTIFY_STORAGE_KEY);
    if (!raw) return new Set<string>();
    const arr = JSON.parse(raw);
    return new Set<string>(Array.isArray(arr) ? (arr as string[]) : []);
  } catch {
    return new Set<string>();
  }
}
function writeNotifySet(set: Set<string>) {
  try {
    window.localStorage.setItem(NOTIFY_STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore */
  }
}

function getConnectionErrorHint(raw: string, connectorType: string): string {
  const msg = raw.toLowerCase();
  if (msg.includes('authentication') || msg.includes('password') || msg.includes('credentials') || msg.includes('permission denied') || msg.includes('access denied') || msg.includes('invalid key')) {
    if (connectorType === 'bigquery') return 'Check that your service account JSON is correct and that the service account has BigQuery Data Viewer and Job User roles.';
    return 'Check your username and password - authentication failed.';
  }
  if (msg.includes('timeout') || msg.includes('timed out') || msg.includes('unreachable') || msg.includes('no route') || msg.includes('network')) {
    return 'The host is unreachable. Check the host/port and ensure your database allows connections from this IP address.';
  }
  if (msg.includes('connection refused')) {
    return 'Connection was refused. Verify the host and port are correct and the database server is running.';
  }
  if (msg.includes('database') && (msg.includes('does not exist') || msg.includes('not found') || msg.includes('unknown'))) {
    return 'The database name was not found. Double-check the database field.';
  }
  if (msg.includes('ssl') || msg.includes('certificate')) {
    return 'SSL/TLS error. Try toggling the SSL setting or check your certificate configuration.';
  }
  if (msg.includes('json') || msg.includes('parse')) {
    return 'The service account JSON appears malformed. Re-download it from Google Cloud Console.';
  }
  if (connectorType === 'snowflake' && (msg.includes('account') || msg.includes('organization'))) {
    return 'Check your Snowflake account identifier - it should look like "orgname-accountname" or "account.region".';
  }
  return 'Double-check your credentials and network settings, then try again.';
}

export default function ConnectorsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { shouldShow, currentStepId } = useProductOnboarding();
  const navigate = useNavigate();
  const { notify } = React.useContext(SnackbarContext);
  const { runningJob } = React.useContext(SyncJobContext);
  // Anonymous "explore" users must verify their email before connecting their
  // own data; the add-connector entry points run through this gate.
  const { requireClaim } = useClaimGate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [connectorTypes, setConnectorTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPostgresForm, setShowPostgresForm] = useState(false);
  const [showGoogleDriveModal, setShowGoogleDriveModal] = useState(false);
  const [googleDriveName, setGoogleDriveName] = useState("");
  const [googleDriveErr, setGoogleDriveErr] = useState<string | null>(null);
  const [showShopifyModal, setShowShopifyModal] = useState(false);
  const [shopifyName, setShopifyName] = useState("");
  const [shopifyShop, setShopifyShop] = useState("");
  const [shopifyErr, setShopifyErr] = useState<string | null>(null);
  const [showHubSpotModal, setShowHubSpotModal] = useState(false);
  const [hubspotName, setHubspotName] = useState("");
  const [hubspotErr, setHubspotErr] = useState<string | null>(null);
  const [showAirtableModal, setShowAirtableModal] = useState(false);
  const [showAgencyZoomModal, setShowAgencyZoomModal] = useState(false);
  const [azForm, setAzForm] = useState({ name: 'AgencyZoom', email: '', password: '' });
  const [azErr, setAzErr] = useState<string | null>(null);
  const [azBusy, setAzBusy] = useState(false);
  const [airtableName, setAirtableName] = useState("");
  const [airtableErr, setAirtableErr] = useState<string | null>(null);
  const [currentConnectorType, setCurrentConnectorType] = useState<string>('');
  // Removed tabs - now showing new connector by default with Your Connectors below

  // Delete states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectorToDelete, setConnectorToDelete] = useState<Connector | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Quick actions menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuConnector, setMenuConnector] = useState<Connector | null>(null);

  // Add Connector modal
  const [addConnectorOpen, setAddConnectorOpen] = useState(false);
  const [addConnectorSearch, setAddConnectorSearch] = useState('');
  // Index of the currently keyboard-highlighted card in the picker. -1 = none.
  const [addConnectorHighlight, setAddConnectorHighlight] = useState<number>(-1);
  // localStorage-backed set of connector slugs the user upvoted ("notify me").
  const [notifySet, setNotifySet] = useState<Set<string>>(() => readNotifySet());
  const toggleNotify = (value: string) => {
    setNotifySet((prev: Set<string>) => {
      const next = new Set<string>(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      writeNotifySet(next);
      return next;
    });
  };
  const addConnectorBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const buildModelBtnRef = React.useRef<HTMLButtonElement | null>(null);

  // Save an AgencyZoom connector. The server signs in with it first: a login
  // AgencyZoom refuses is saved as Failed and its reason shown here.
  const connectAgencyZoom = async () => {
    setAzErr(null);
    if (!tenantId) { setAzErr('Tenant not found.'); return; }
    setAzBusy(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'agencyzoom',
          name: azForm.name.trim(),
          config: { email: azForm.email.trim(), password: azForm.password },
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data?.detail || 'Could not add AgencyZoom';
        throw new Error(Array.isArray(detail) ? detail.map((d: any) => d.msg || String(d)).join('; ') : String(detail));
      }
      if (data.status === 'failed') {
        // Not kept: a failed login would hold the name, and the next try would be refused as a duplicate.
        if (data.connector_id) {
          await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${data.connector_id}`), {
            method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
          }).catch(() => undefined);
        }
        setAzErr(`${data.error || 'AgencyZoom did not accept that login.'}\n\nCheck the email and password, then try again.`);
        return;
      }
      fetchConnectors();
      trackConnectorAdded('agencyzoom');
      markProductStep('connector_added');
      setShowAgencyZoomModal(false);
    } catch (err) {
      setAzErr(err instanceof Error ? err.message : 'Could not add AgencyZoom');
    } finally {
      setAzBusy(false);
    }
  };

  // Open the right next-step dialog for a chosen connector type. Used by both
  // card click and Enter-key activation in the picker.
  const pickConnectorType = (typeValue: string) => {
    const ct = connectorTypes.find((x: any) => x.value === typeValue);
    if (!ct) return;
    setAddConnectorOpen(false);
    // Signed in with an AgencyZoom user's email and password, in a dialog of its own.
    if (typeValue === 'agencyzoom') {
      setAzForm({ name: 'AgencyZoom', email: '', password: '' });
      setAzErr(null);
      setShowAgencyZoomModal(true);
      return;
    }
    if (ct.requires_oauth) {
      if (typeValue === 'google_drive') setShowGoogleDriveModal(true);
      else if (typeValue === 'shopify') setShowShopifyModal(true);
      else if (typeValue === 'hubspot') setShowHubSpotModal(true);
      else if (typeValue === 'airtable') setShowAirtableModal(true);
      return;
    }
    setCurrentConnectorType(typeValue);
    if (typeValue !== 'snowflake' && typeValue !== 'bigquery') {
      let defaultPort = '5432';
      if (typeValue === 'mysql') defaultPort = '3306';
      else if (typeValue === 'redshift') defaultPort = '5439';
      setForm((prev: any) => ({ ...prev, port: defaultPort }));
    }
    setAddErr(null);
    setShowPostgresForm(true);
  };

  // Open the Add Connector picker, gated for anonymous users: connecting your
  // own data requires a verified account, so guests get the claim modal first.
  const openAddConnector = (search = '') => {
    requireClaim('connect_own_data', () => {
      setShowGoogleDriveModal(false);
      setShowShopifyModal(false);
      setShowHubSpotModal(false);
      setAddConnectorOpen(true);
      setAddConnectorSearch(search);
    });
  };

  // Re-open the picker (used by "Back" buttons inside the next-step dialogs).
  const backToPicker = () => {
    setShowGoogleDriveModal(false);
    setShowShopifyModal(false);
    setShowHubSpotModal(false);
    setShowAirtableModal(false);
    setShowPostgresForm(false);
    setAddConnectorOpen(true);
  };

  // Activate / deactivate
  const [togglingActive, setTogglingActive] = useState(false);

  // Modal state
  const [addLoading, setAddLoading] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    host: '',
    port: '',
    user: '',
    password: '',
    database: '',
    mfa_type: '',
    account: '',
    schema: '',
    warehouse: '',
    role: '',
    // Snowflake authentication
    auth_method: 'password', // 'password' | 'key_pair'
    private_key: '',
    private_key_passphrase: '',
    // BigQuery configuration
    service_account_json: '',
    project: '',
    // SSH tunnel configuration
    ssh_enabled: false,
    ssh_host: '',
    ssh_port: '22',
    ssh_user: '',
    ssh_auth_method: 'password', // 'password' | 'key'
    ssh_password: '',
    ssh_private_key: '',
    ssh_private_key_passphrase: '',
  });

  useEffect(() => {
    const tStr = window.localStorage.getItem('klaris_tenant');
    if (!tStr) {
      // Preserve the deep link so post-login lands the user back here.
      navigate(
        buildLoginUrl('/login', window.location.pathname + window.location.search),
        { replace: true },
      );
      return;
    }
    const t = JSON.parse(tStr);
    setTenantId(t.tenant_id);
  }, [navigate]);

  useEffect(() => {
    if (tenantId) {
      fetchConnectors();
        // Initialize connector types with categories and popular flags
        setConnectorTypes([
          { value: 'postgres', label: 'PostgreSQL', icon: PostgresSvg, description: 'Relational database for structured data', requires_oauth: false, category: 'database', popular: true,
            keywords: ['sql', 'rdbms', 'relational', 'database', 'pg', 'psql', 'open source'] },
          { value: 'mysql', label: 'MySQL', icon: MySQLSvg, description: 'Open-source relational database', requires_oauth: false, category: 'database', popular: true,
            keywords: ['sql', 'rdbms', 'relational', 'database', 'mariadb', 'open source'] },
          { value: 'snowflake', label: 'Snowflake', icon: SnowflakeSvg, description: 'Cloud data platform and warehouse', requires_oauth: false, category: 'database', popular: true,
            keywords: ['warehouse', 'olap', 'analytics', 'cloud', 'columnar', 'sql'] },
          { value: 'redshift', label: 'AWS Redshift', icon: RedshiftSvg, description: 'Amazon Redshift data warehouse', requires_oauth: false, category: 'database', popular: true,
            keywords: ['warehouse', 'aws', 'amazon', 'olap', 'analytics', 'columnar', 'sql'] },
          { value: 'bigquery', label: 'BigQuery', icon: BigQuerySvg, description: 'Google Cloud data warehouse', requires_oauth: false, category: 'database', popular: true,
            keywords: ['warehouse', 'gcp', 'google cloud', 'olap', 'analytics', 'sql'] },
          { value: 'google_drive', label: 'Google Drive', icon: GoogleDriveSvg, description: 'Cloud storage for files and spreadsheets', requires_oauth: true, category: 'files', popular: true,
            keywords: ['gdrive', 'sheets', 'spreadsheet', 'csv', 'excel', 'documents', 'storage'] },
          { value: 'shopify', label: 'Shopify', icon: ShopifySvg, description: 'E-commerce platform - orders, products & customers', requires_oauth: true, category: 'api', popular: true,
            keywords: ['ecommerce', 'commerce', 'orders', 'products', 'customers', 'store'] },
          { value: 'hubspot', label: 'HubSpot CRM', icon: HubSpotSvg, description: 'CRM platform - contacts, deals, companies & tickets', requires_oauth: true, category: 'api', popular: true,
            keywords: ['crm', 'contacts', 'deals', 'sales', 'marketing', 'tickets'] },
          { value: 'airtable', label: 'Airtable', icon: AirtableSvg, description: 'Collaborative spreadsheet-database — bases, tables & records', requires_oauth: true, category: 'api', popular: true,
            keywords: ['spreadsheet', 'base', 'tables', 'records', 'no-code', 'database'] },
          { value: 'agencyzoom', label: 'AgencyZoom', icon: AgencyZoomSvg, description: 'Agency CRM: your team, their tasks and calendars', requires_oauth: false, category: 'api', popular: false,
            keywords: ['crm', 'insurance', 'agency', 'tasks', 'calendar', 'callbacks'] }
        ]);
    }
    // eslint-disable-next-line
  }, [tenantId]);

  const fetchConnectors = async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Failed to fetch connectors');
      const data = await resp.json();
      setConnectors(data.connectors || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch connectors');
    } finally {
      setLoading(false);
    }
  };


  const handleAddConnector = async () => {
    setAddErr(null);
    
    // Validate required fields based on connector type
    if (currentConnectorType === 'snowflake') {
      if (!form.account || !form.user || !form.database) {
        setAddErr('Account, User, and Database are required for Snowflake.');
        return;
      }
      // Check authentication method
      if (form.auth_method === 'password') {
        if (!form.password) {
          setAddErr('Password is required for password authentication.');
          return;
        }
      } else if (form.auth_method === 'key_pair') {
        if (!form.private_key) {
          setAddErr('Private key is required for key pair authentication.');
          return;
        }
      }
    } else if (currentConnectorType === 'redshift') {
      // Redshift validation (similar to Postgres)
      if (!form.host || !form.user || !form.password || !form.database) {
        setAddErr('Host, User, Password, and Database are required for Redshift.');
        return;
      }
    } else if (currentConnectorType === 'bigquery') {
      if (!form.service_account_json) {
        setAddErr('Service Account JSON is required for BigQuery.');
        return;
      }
      // Validate JSON
      try {
        JSON.parse(form.service_account_json);
      } catch {
        setAddErr('Service Account JSON is not valid JSON.');
        return;
      }
    } else {
      // Postgres or MySQL
      if (!form.host || !form.user || !form.password || !form.database) {
        setAddErr('Host, User, Password, and Database are required.');
        return;
      }
      // Validate SSH tunnel if enabled
      if (form.ssh_enabled) {
        if (!form.ssh_host || !form.ssh_user) {
          setAddErr('SSH Host and SSH User are required when SSH tunnel is enabled.');
          return;
        }
        if (form.ssh_auth_method === 'password' && !form.ssh_password) {
          setAddErr('SSH Password is required for SSH password authentication.');
          return;
        }
        if (form.ssh_auth_method === 'key' && !form.ssh_private_key) {
          setAddErr('SSH Private Key is required for SSH key authentication.');
          return;
        }
      }
    }
    
    setAddLoading(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      
      let config: any = {};
      
      if (currentConnectorType === 'snowflake') {
        config = {
          account: form.account,
          user: form.user,
          database: form.database,
          schema: form.schema || 'PUBLIC',
          warehouse: form.warehouse || undefined,
          role: form.role || undefined,
        };
        
        // Add authentication based on method
        if (form.auth_method === 'password') {
          config.password = form.password;
        } else if (form.auth_method === 'key_pair') {
          config.private_key = form.private_key;
          if (form.private_key_passphrase) {
            config.private_key_passphrase = form.private_key_passphrase;
          }
        }
      } else if (currentConnectorType === 'redshift') {
        // Redshift (similar to Postgres but different default port)
        config = {
          host: form.host,
          port: form.port || '5439',  // Redshift default port
          user: form.user,
          password: form.password,
          database: form.database,
          schema: form.schema || 'public',  // Default to public for backward compat
        };
        
        // SSH tunnel support (optional for Redshift)
        if (form.ssh_enabled) {
          config.ssh_tunnel = {
            // The backend gates tunnelling on this flag; without it the rest of
            // the block is saved but never used.
            enabled: true,
            ssh_host: form.ssh_host,
            ssh_port: form.ssh_port || '22',
            ssh_user: form.ssh_user,
            ssh_auth_method: form.ssh_auth_method || 'password',
          };
          
          if (form.ssh_auth_method === 'password') {
            config.ssh_tunnel.ssh_password = form.ssh_password;
          } else if (form.ssh_auth_method === 'key') {
            config.ssh_tunnel.ssh_private_key = form.ssh_private_key;
            if (form.ssh_private_key_passphrase) {
              config.ssh_tunnel.ssh_private_key_passphrase = form.ssh_private_key_passphrase;
            }
          }
        }
      } else if (currentConnectorType === 'bigquery') {
        config = {
          service_account_json: form.service_account_json,
          project: form.project || undefined,
        };
      } else {
        // Postgres or MySQL
        config = {
          host: form.host,
          port: form.port,
          user: form.user,
          password: form.password,
          database: form.database,
          mfa_type: form.mfa_type || undefined,
        };
        
        // Add SSH tunnel configuration if enabled
        if (form.ssh_enabled) {
          config.ssh_tunnel = {
            enabled: true,
            ssh_host: form.ssh_host,
            ssh_port: form.ssh_port || '22',
            ssh_user: form.ssh_user,
          };
          
          if (form.ssh_auth_method === 'password') {
            config.ssh_tunnel.ssh_password = form.ssh_password;
          } else if (form.ssh_auth_method === 'key') {
            config.ssh_tunnel.ssh_private_key = form.ssh_private_key;
            if (form.ssh_private_key_passphrase) {
              config.ssh_tunnel.ssh_private_key_passphrase = form.ssh_private_key_passphrase;
            }
          }
        }
      }
      
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: currentConnectorType,
          name: form.name || undefined,
          config: config
        })
      });
      const data = await resp.json();
      if (!resp.ok) {
        const detail = data?.detail || data?.error || 'Failed to add connector';
        throw new Error(Array.isArray(detail) ? detail.map((d: any) => d.msg || String(d)).join('; ') : String(detail));
      }
      // Connector was created but connection test failed
      if (data.status === 'failed') {
        const raw = data.error || 'Connection test failed.';
        const hint = getConnectionErrorHint(raw, currentConnectorType);
        setAddErr(`${raw}\n\n${hint}`);
        fetchConnectors(); // refresh list so the failed connector appears
        setAddLoading(false);
        return;
      }
      trackConnectorAdded(currentConnectorType);
      // Fire-and-forget: immediately refresh onboarding widget state
      markProductStep('connector_added');
      setShowPostgresForm(false);
      setForm({ 
        name: '', host: '', port: '', user: '', password: '', database: '', mfa_type: '', 
        account: '', schema: '', warehouse: '', role: '',
        auth_method: 'password', private_key: '', private_key_passphrase: '',
        service_account_json: '', project: '',
        ssh_enabled: false, ssh_host: '', ssh_port: '22', ssh_user: '', 
        ssh_auth_method: 'password', ssh_password: '', ssh_private_key: '', ssh_private_key_passphrase: ''
      });
      setCurrentConnectorType('');
      fetchConnectors();
    } catch (err: any) {
      setAddErr(err.message || 'Failed to add connector');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteConnector = async () => {
    if (!connectorToDelete || !tenantId) return;
    setDeleting(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorToDelete.connector_id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data?.error || 'Failed to delete connector');
      }
      // Refresh the connectors list
      await fetchConnectors();
      setDeleteDialogOpen(false);
      setConnectorToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete connector');
    } finally {
      setDeleting(false);
    }
  };
  
  // Quick actions menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, connector: Connector) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuConnector(connector);
  };
  
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuConnector(null);
  };
  
  const handleDeleteClick = () => {
    if (menuConnector) {
      setConnectorToDelete(menuConnector);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleToggleActive = async () => {
    if (!menuConnector || !tenantId) return;
    const connector = menuConnector;
    handleMenuClose();
    setTogglingActive(true);
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const newStatus = connector.status === 'active' ? 'inactive' : 'active';
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connector.connector_id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!resp.ok) throw new Error('Failed to update connector status');
      await fetchConnectors();
      const label = connector.name || connector.type;
      notify(
        newStatus === 'active'
          ? `${label} activated - it will be included in future sync runs.`
          : `${label} deactivated - it will be excluded from future sync runs.`,
        newStatus === 'active' ? 'success' : 'info'
      );
    } catch (err: any) {
      notify(err.message || 'Failed to update status', 'error');
      setError(err.message || 'Failed to update status');
    } finally {
      setTogglingActive(false);
    }
  };

  // ── List view state (filters / sort / group) ─────────────────────────────
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'failed'>('active');
  type SortKey = 'lastSync' | 'created' | 'name' | 'failuresFirst';
  const [sortKey, setSortKey] = useState<SortKey>('lastSync');
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);

  // Aggregate stats for the health strip / filter chip counts.
  const stats = useMemo(() => {
    const counts = { active: 0, inactive: 0, failed: 0, pending: 0 };
    let mostRecent: string | null = null;
    for (const c of connectors) {
      const k = normalizeStatus(c.status);
      counts[k]++;
      if (c.last_schema_fetch) {
        if (!mostRecent || new Date(c.last_schema_fetch).getTime() > new Date(mostRecent).getTime()) {
          mostRecent = c.last_schema_fetch;
        }
      }
    }
    return { ...counts, total: connectors.length, lastSync: mostRecent };
  }, [connectors]);

  const filteredConnectors = useMemo(() => {
    const text = filterText.trim().toLowerCase();
    const filtered = connectors.filter((c: Connector) => {
      const matchesText = !text
        || (c.name || `${c.type} Connector`).toLowerCase().includes(text)
        || c.type.toLowerCase().includes(text)
        || prettyType(c.type).toLowerCase().includes(text);
      const matchesStatus = statusFilter === 'all' || normalizeStatus(c.status) === statusFilter;
      return matchesText && matchesStatus;
    });

    const tsOf = (c: Connector) => (c.last_schema_fetch ? new Date(c.last_schema_fetch).getTime() : 0);
    const created = (c: Connector) => (c.created_at ? new Date(c.created_at).getTime() : 0);
    const statusRank: Record<ConnectorStatusKey, number> = { failed: 0, pending: 1, inactive: 2, active: 3 };
    const sorted = [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return (a.name || a.type).localeCompare(b.name || b.type);
        case 'created':
          return created(b) - created(a);
        case 'failuresFirst':
          return (statusRank[normalizeStatus(a.status)] - statusRank[normalizeStatus(b.status)]) || (tsOf(b) - tsOf(a));
        case 'lastSync':
        default:
          return tsOf(b) - tsOf(a);
      }
    });
    return sorted;
  }, [connectors, filterText, statusFilter, sortKey]);

  // Bucket the filtered+sorted connectors by category for grouped rendering.
  const groupedConnectors = useMemo(() => {
    const buckets: Record<CategoryKey, Connector[]> = { database: [], files: [], api: [], other: [] };
    for (const c of filteredConnectors) buckets[categorizeConnector(c)].push(c);
    return (Object.keys(buckets) as CategoryKey[])
      .filter((k) => buckets[k].length > 0)
      .map((k) => ({ key: k, items: buckets[k] }));
  }, [filteredConnectors]);

  const hasConnectors = connectors.length > 0;
  const sortLabels: Record<SortKey, string> = {
    lastSync: 'Recently synced',
    created: 'Recently added',
    name: 'Name (A-Z)',
    failuresFirst: 'Failures first',
  };

  // Connectors currently part of an in-flight global sync job (read from
  // SyncJobContext) — used to show a spinner on those rows.
  const liveSyncIds = useMemo(() => {
    const set = new Set<string>();
    if (runningJob?.connector_ids) runningJob.connector_ids.forEach((id) => set.add(id));
    return set;
  }, [runningJob]);

  return (
    <Box>
      {/* Page header — matches the platform-wide toolbar pattern: subtitle on
          the left, primary actions on the right with a bottom divider. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.25, md: 2 },
          mb: 2,
          pb: 1.5,
          borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
          minHeight: { md: 36 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.8125rem',
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            lineHeight: 1.45,
            flex: { md: '0 1 auto' },
            minWidth: 0,
          }}
        >
          Connect your data sources and unify them into a model Copilot can query
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: { xs: 'stretch', md: 'flex-end' },
            width: { xs: '100%', md: 'auto' },
            flexWrap: 'wrap',
          }}
        >
          <Tooltip
            title={hasConnectors ? '' : 'Add a connector first to build a data model.'}
            disableHoverListener={hasConnectors}
            disableFocusListener={hasConnectors}
            disableTouchListener={hasConnectors}
          >
            <span style={{ display: 'inline-flex', flex: isMobile ? 1 : undefined }}>
              <Button
                ref={buildModelBtnRef}
                variant="outlined"
                size="small"
                startIcon={<AccountTreeRounded />}
                disabled={!hasConnectors}
                onClick={() => navigate('/data-sync')}
                sx={{ fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
              >
                {isMobile ? 'Data model' : 'Build data model'}
              </Button>
            </span>
          </Tooltip>
          <OnboardingCoachmark
            open={Boolean(shouldShow && currentStepId === 'data_model_built' && hasConnectors)}
            anchorRef={buildModelBtnRef}
            message="Great. Now build your data model."
            placement="bottom-end"
          />

          <span style={{ display: 'inline-flex', flex: isMobile ? 1 : undefined }}>
            <Button
              ref={addConnectorBtnRef}
              variant="contained"
              size="small"
              startIcon={<AddRounded />}
              onClick={() => openAddConnector('')}
              sx={{ fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
            >
              {isMobile ? 'Add' : 'New connector'}
            </Button>
          </span>
          <OnboardingCoachmark
            open={Boolean(
              !isMobile &&
                shouldShow &&
                currentStepId === 'connector_added' &&
                !addConnectorOpen &&
                !showPostgresForm &&
                !showGoogleDriveModal &&
                !showShopifyModal &&
                !showHubSpotModal,
            )}
            anchorRef={addConnectorBtnRef}
            message="Start here: add your first connector."
            placement="bottom-end"
          />
        </Stack>
      </Box>

      {/* Add Connector Dialog — command-palette-style picker. */}
      <AddConnectorDialog
        open={addConnectorOpen}
        onClose={() => {
          setAddConnectorOpen(false);
          setAddConnectorSearch('');
          setAddConnectorHighlight(-1);
        }}
        connectorTypes={connectorTypes}
        comingSoon={COMING_SOON_CONNECTORS}
        existingTypes={new Set(connectors.map((c: Connector) => c.type))}
        search={addConnectorSearch}
        onSearchChange={setAddConnectorSearch}
        highlight={addConnectorHighlight}
        setHighlight={setAddConnectorHighlight}
        onPick={pickConnectorType}
        notifySet={notifySet}
        onToggleNotify={toggleNotify}
      />

      <AppDialog
        open={showGoogleDriveModal}
        onClose={() => { setShowGoogleDriveModal(false); setGoogleDriveName(""); setGoogleDriveErr(null); }}
        onBack={() => { setGoogleDriveName(""); setGoogleDriveErr(null); backToPicker(); }}
        size="sm"
        title="Google Drive connector"
        primaryAction={{
          label: 'Proceed to OAuth',
          onClick: () => {
            setGoogleDriveErr(null);
            if (!googleDriveName.trim()) { setGoogleDriveErr("Connector name is required."); return; }
            if (!tenantId) { setGoogleDriveErr("Tenant not found."); return; }
            const url = `${API_URL}/api/v1/connectors/google-drive/authorize?tenant_id=${tenantId}&name=${encodeURIComponent(googleDriveName.trim())}`;
            window.location.href = url;
          },
        }}
        secondaryAction={{
          label: 'Cancel',
          color: 'inherit',
          onClick: () => { setShowGoogleDriveModal(false); setGoogleDriveName(""); setGoogleDriveErr(null); },
        }}
      >
        <TextField
          label="Connector Name"
          placeholder="Enter a name for this connection"
          fullWidth
          margin="normal"
          value={googleDriveName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGoogleDriveName(e.target.value)}
          required
          autoComplete="off"
        />
        {googleDriveErr && <Alert severity="error" sx={{ mt: 1 }}>{googleDriveErr}</Alert>}
      </AppDialog>

      <AppDialog
        open={showShopifyModal}
        onClose={() => { setShowShopifyModal(false); setShopifyName(""); setShopifyShop(""); setShopifyErr(null); }}
        onBack={() => { setShopifyName(""); setShopifyShop(""); setShopifyErr(null); backToPicker(); }}
        size="sm"
        title="Connect Shopify store"
        primaryAction={{
          label: 'Connect to Shopify',
          onClick: () => {
            setShopifyErr(null);
            if (!shopifyName.trim()) { setShopifyErr("Connector name is required."); return; }
            if (!shopifyShop.trim()) { setShopifyErr("Shop domain is required."); return; }
            if (!tenantId) { setShopifyErr("Tenant not found."); return; }
            const url = `${API_URL}/api/v1/connectors/shopify/authorize?tenant_id=${tenantId}&shop=${encodeURIComponent(shopifyShop.trim())}&name=${encodeURIComponent(shopifyName.trim())}`;
            window.location.href = url;
          },
        }}
        secondaryAction={{
          label: 'Cancel',
          color: 'inherit',
          onClick: () => { setShowShopifyModal(false); setShopifyName(""); setShopifyShop(""); setShopifyErr(null); },
        }}
      >
        <TextField
          label="Connector Name"
          placeholder="e.g. My Shopify Store"
          fullWidth
          margin="normal"
          value={shopifyName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShopifyName(e.target.value)}
          required
          autoComplete="off"
        />
        <TextField
          label="Shop Domain"
          placeholder="your-store.myshopify.com"
          fullWidth
          margin="normal"
          value={shopifyShop}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShopifyShop(e.target.value)}
          required
          autoComplete="off"
          helperText="Enter your Shopify store domain (e.g. your-store or your-store.myshopify.com)"
        />
        {shopifyErr && <Alert severity="error" sx={{ mt: 1 }}>{shopifyErr}</Alert>}
      </AppDialog>

      <AppDialog
        open={showHubSpotModal}
        onClose={() => { setShowHubSpotModal(false); setHubspotName(""); setHubspotErr(null); }}
        onBack={() => { setHubspotName(""); setHubspotErr(null); backToPicker(); }}
        size="sm"
        title="Connect HubSpot CRM"
        primaryAction={{
          label: 'Connect to HubSpot',
          onClick: () => {
            setHubspotErr(null);
            if (!hubspotName.trim()) { setHubspotErr("Connector name is required."); return; }
            if (!tenantId) { setHubspotErr("Tenant not found."); return; }
            const url = `${API_URL}/api/v1/connectors/hubspot/authorize?tenant_id=${tenantId}&name=${encodeURIComponent(hubspotName.trim())}`;
            window.location.href = url;
          },
        }}
        secondaryAction={{
          label: 'Cancel',
          color: 'inherit',
          onClick: () => { setShowHubSpotModal(false); setHubspotName(""); setHubspotErr(null); },
        }}
      >
        <TextField
          label="Connector Name"
          placeholder="e.g. My HubSpot CRM"
          fullWidth
          margin="normal"
          value={hubspotName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHubspotName(e.target.value)}
          required
          autoComplete="off"
          name="hubspot_connector_name"
          inputProps={{ autoComplete: 'off' }}
        />
        {hubspotErr && <Alert severity="error" sx={{ mt: 1 }}>{hubspotErr}</Alert>}
      </AppDialog>

      <AppDialog
        open={showAirtableModal}
        onClose={() => { setShowAirtableModal(false); setAirtableName(''); setAirtableErr(null); }}
        onBack={() => { setAirtableName(''); setAirtableErr(null); backToPicker(); }}
        size="sm"
        title="Connect Airtable"
        primaryAction={{
          label: 'Connect to Airtable',
          onClick: () => {
            setAirtableErr(null);
            if (!airtableName.trim()) { setAirtableErr('Connector name is required.'); return; }
            if (!tenantId) { setAirtableErr('Tenant not found.'); return; }
            const url = `${API_URL}/api/v1/connectors/airtable/authorize?tenant_id=${tenantId}&name=${encodeURIComponent(airtableName.trim())}`;
            window.location.href = url;
          },
        }}
        secondaryAction={{
          label: 'Cancel',
          color: 'inherit',
          onClick: () => { setShowAirtableModal(false); setAirtableName(''); setAirtableErr(null); },
        }}
      >
        <TextField
          label="Connector Name"
          placeholder="e.g. My Airtable"
          fullWidth
          margin="normal"
          value={airtableName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAirtableName(e.target.value)}
          required
          autoComplete="off"
          inputProps={{ autoComplete: 'off' }}
        />
        {airtableErr && <Alert severity="error" sx={{ mt: 1 }}>{airtableErr}</Alert>}
      </AppDialog>

      <AppDialog
        open={showAgencyZoomModal}
        onClose={() => { if (!azBusy) setShowAgencyZoomModal(false); }}
        onBack={() => { setAzErr(null); setShowAgencyZoomModal(false); backToPicker(); }}
        size="sm"
        title="Connect AgencyZoom"
        primaryAction={{
          label: 'Connect',
          loading: azBusy,
          disabled: !azForm.name.trim() || !azForm.email.trim() || !azForm.password,
          onClick: connectAgencyZoom,
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => setShowAgencyZoomModal(false) }}
      >
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, mb: 1 })}>
          Sign in with an AgencyZoom user. Use the agency owner&apos;s login, so every team member can be given
          callbacks. We check the login works before saving it, and the password is never shown again.
        </Typography>
        <TextField label="Connector name" fullWidth margin="dense" value={azForm.name} required autoComplete="off"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzForm((f) => ({ ...f, name: e.target.value }))} />
        <TextField label="AgencyZoom email" type="email" fullWidth margin="dense" value={azForm.email} required
          autoComplete="off" name="agencyzoom_email"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzForm((f) => ({ ...f, email: e.target.value }))} />
        <TextField label="AgencyZoom password" type="password" fullWidth margin="dense" value={azForm.password} required
          autoComplete="new-password" name="agencyzoom_password"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAzForm((f) => ({ ...f, password: e.target.value }))} />
        {azErr && <Alert severity="error" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{azErr}</Alert>}
      </AppDialog>

      {/* Connectors workspace */}
      <Box sx={{ mt: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Health strip — counts + last sync. Cards filter the list. ─── */}
        {hasConnectors && (
          <Box
            sx={{
              mb: 2,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              gap: 1.25,
            }}
          >
            <HealthCard
              tone="success"
              icon={<CheckCircleRounded sx={{ fontSize: 20 }} />}
              count={stats.active}
              label="Healthy"
              hint="Included in syncs"
              active={statusFilter === 'active'}
              onClick={stats.active > 0 ? () => setStatusFilter(statusFilter === 'active' ? 'all' : 'active') : undefined}
            />
            <HealthCard
              tone="neutral"
              icon={<PauseCircleOutlineRounded sx={{ fontSize: 20 }} />}
              count={stats.inactive}
              label="Paused"
              hint="Excluded from syncs"
              active={statusFilter === 'inactive'}
              onClick={stats.inactive > 0 ? () => setStatusFilter(statusFilter === 'inactive' ? 'all' : 'inactive') : undefined}
            />
            <HealthCard
              tone="error"
              icon={<ErrorOutlineRounded sx={{ fontSize: 20 }} />}
              count={stats.failed}
              label="Failed"
              hint={stats.failed > 0 ? 'Need attention' : 'All clear'}
              emphasize={stats.failed > 0}
              active={statusFilter === 'failed'}
              onClick={stats.failed > 0 ? () => setStatusFilter(statusFilter === 'failed' ? 'all' : 'failed') : undefined}
            />
            <HealthCard
              tone="info"
              icon={<ScheduleRounded sx={{ fontSize: 20 }} />}
              count={stats.lastSync ? formatRelativeTime(stats.lastSync) : '—'}
              label="Last sync"
              hint={stats.lastSync ? 'Across all connectors' : 'No syncs yet'}
              valueIsText
            />
          </Box>
        )}

        {/* ── Toolbar: search + filter chips + sort + group ───────────── */}
        {hasConnectors && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            sx={{ mb: 1.5, alignItems: { xs: 'stretch', md: 'center' } }}
          >
            <TextField
              placeholder="Search connectors…"
              size="small"
              value={filterText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={(t: any) => ({ fontSize: 18, color: t.tokens.text.tertiary })} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', md: 320 }, flex: '0 0 auto' }}
            />

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ flexShrink: 0, overflowX: 'auto', pb: 0.25, '&::-webkit-scrollbar': { height: 4 } }}
            >
              <FilterChip label="All" count={stats.total} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
              <FilterChip label="Active" count={stats.active} active={statusFilter === 'active'} onClick={() => setStatusFilter('active')} />
              <FilterChip label="Paused" count={stats.inactive} active={statusFilter === 'inactive'} onClick={() => setStatusFilter('inactive')} />
              <FilterChip label="Failed" count={stats.failed} active={statusFilter === 'failed'} tone="error" onClick={() => setStatusFilter('failed')} />
            </Stack>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Tooltip title={groupByCategory ? 'Show flat list' : 'Group by source type'}>
                <Button
                  size="small"
                  variant={groupByCategory ? 'outlined' : 'text'}
                  onClick={() => setGroupByCategory((v) => !v)}
                  sx={(t: any) => ({
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: t.tokens.text.secondary,
                    borderColor: t.tokens.border.default,
                  })}
                >
                  {groupByCategory ? 'Grouped' : 'Flat'}
                </Button>
              </Tooltip>
              <Button
                size="small"
                variant="text"
                startIcon={<SortRounded sx={{ fontSize: 16 }} />}
                onClick={(e) => setSortAnchor(e.currentTarget)}
                sx={(t: any) => ({
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  color: t.tokens.text.secondary,
                })}
              >
                {sortLabels[sortKey]}
              </Button>
              <Menu
                anchorEl={sortAnchor}
                open={Boolean(sortAnchor)}
                onClose={() => setSortAnchor(null)}
                PaperProps={{ sx: (t: any) => ({ borderRadius: `${t.ds.radius.md}px`, minWidth: 200, mt: 0.5 }) }}
              >
                {(Object.keys(sortLabels) as SortKey[]).map((k) => (
                  <MenuItem
                    key={k}
                    selected={sortKey === k}
                    onClick={() => { setSortKey(k); setSortAnchor(null); }}
                    sx={{ fontSize: '0.8125rem', py: 0.875 }}
                  >
                    {sortLabels[k]}
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          </Stack>
        )}

        {/* ── List / Empty state ─────────────────────────────────────── */}
        {!hasConnectors && !loading ? (
          <EmptyConnectorsHero
            onPickType={(typeValue) => requireClaim('connect_own_data', () => pickConnectorType(typeValue))}
            onSeeAll={() => openAddConnector('')}
            connectorTypes={connectorTypes}
          />
        ) : (
          <Paper
            variant="outlined"
            sx={(theme) => ({
              borderRadius: `${theme.ds.radius.md}px`,
              border: `1px solid ${theme.tokens.border.subtle}`,
              overflow: 'hidden',
              boxShadow: theme.tokens.shadow.sm,
            })}
          >
            {loading ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            ) : filteredConnectors.length === 0 ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No connectors match your filters.
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => { setFilterText(''); setStatusFilter('all'); }}
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  Clear filters
                </Button>
              </Box>
            ) : groupByCategory ? (
              groupedConnectors.map((group, gi) => (
                <Box key={group.key}>
                  <Box
                    sx={(t) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2.5,
                      py: 1.25,
                      bgcolor: t.tokens.surface.sunken,
                      borderTop: gi === 0 ? 'none' : `1px solid ${t.tokens.border.subtle}`,
                      borderBottom: `1px solid ${t.tokens.border.subtle}`,
                    })}
                  >
                    {React.createElement(CATEGORY_META[group.key].icon, {
                      sx: (t: any) => ({ fontSize: 16, color: t.tokens.text.tertiary }),
                    })}
                    <Typography
                      sx={(t) => ({
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: t.tokens.text.tertiary,
                      })}
                    >
                      {CATEGORY_META[group.key].label}
                    </Typography>
                    <Typography
                      sx={(t) => ({ fontSize: '0.75rem', color: t.tokens.text.tertiary, fontWeight: 500 })}
                    >
                      · {group.items.length}
                    </Typography>
                  </Box>
                  {group.items.map((c, idx) => (
                    <ConnectorRow
                      key={c.connector_id}
                      connector={c}
                      isLast={idx === group.items.length - 1}
                      isMobile={isMobile}
                      isSyncing={liveSyncIds.has(c.connector_id)}
                      onOpen={() => navigate(`/connectors/${c.connector_id}`)}
                      onMenu={(e) => handleMenuOpen(e, c)}
                    />
                  ))}
                </Box>
              ))
            ) : (
              filteredConnectors.map((c, idx) => (
                <ConnectorRow
                  key={c.connector_id}
                  connector={c}
                  isLast={idx === filteredConnectors.length - 1}
                  isMobile={isMobile}
                  isSyncing={liveSyncIds.has(c.connector_id)}
                  onOpen={() => navigate(`/connectors/${c.connector_id}`)}
                  onMenu={(e) => handleMenuOpen(e, c)}
                />
              ))
            )}
          </Paper>
        )}

      </Box>

      {showPostgresForm && (
        <AppDialog
          open={showPostgresForm}
          onClose={() => { setShowPostgresForm(false); setCurrentConnectorType(''); setAddErr(null); }}
          onBack={() => { setAddErr(null); setCurrentConnectorType(''); backToPicker(); }}
          size="md"
          title={`Configure ${currentConnectorType === 'mysql' ? 'MySQL' : currentConnectorType === 'postgres' ? 'PostgreSQL' : currentConnectorType === 'snowflake' ? 'Snowflake' : currentConnectorType === 'redshift' ? 'AWS Redshift' : currentConnectorType === 'bigquery' ? 'BigQuery' : 'database'} connector`}
          busy={addLoading}
          dividers
          primaryAction={{ label: 'Add Connection', onClick: handleAddConnector }}
          secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => setShowPostgresForm(false) }}
        >
          {addErr && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error" icon={false} sx={{ borderRadius: 1.5 }}>
                {addErr.includes('\n\n') ? (
                  <>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Connection failed
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.75, fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-word' }}>
                      {addErr.split('\n\n')[0].replace(/^Connection (test )?failed:\s*/i, '')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'error.dark' }}>
                      {addErr.split('\n\n')[1]}
                    </Typography>
                  </>
                ) : addErr}
              </Alert>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                {/* Connection Name */}
                <TextField
                  label="Connection Name"
                  placeholder="Enter a name for this connection"
                  fullWidth
                  margin="normal"
                  value={form.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, name: e.target.value }))}
                />

                {/* Snowflake Configuration */}
                {currentConnectorType === 'snowflake' ? (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Database Connection</Typography>
                    
                    <TextField
                      label="Account *"
                      placeholder="myaccount.snowflakecomputing.com"
                      fullWidth
                      margin="normal"
                      value={form.account}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, account: e.target.value }))}
                      required
                      helperText="Your Snowflake account identifier"
                    />
                    <TextField
                      label="User *"
                      placeholder="Enter your username"
                      fullWidth
                      margin="normal"
                      value={form.user}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, user: e.target.value }))}
                      required
                      autoComplete="off"
                    />
                    <TextField
                      label="Database *"
                      placeholder="Enter your database name"
                      fullWidth
                      margin="normal"
                      value={form.database}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, database: e.target.value }))}
                      required
                    />
                    <TextField
                      label="Schema"
                      placeholder="PUBLIC"
                      fullWidth
                      margin="normal"
                      value={form.schema}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, schema: e.target.value }))}
                    />
                    <TextField
                      label="Warehouse"
                      placeholder="Optional"
                      fullWidth
                      margin="normal"
                      value={form.warehouse}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, warehouse: e.target.value }))}
                    />
                    <TextField
                      label="Role"
                      placeholder="Optional"
                      fullWidth
                      margin="normal"
                      value={form.role}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, role: e.target.value }))}
                    />

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Authentication Method</Typography>
                    
                    <TextField
                      select
                      label="Authentication Type"
                      fullWidth
                      margin="normal"
                      value={form.auth_method}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, auth_method: e.target.value }))}
                    >
                      <MenuItem value="password">Username & Password</MenuItem>
                      <MenuItem value="key_pair">Key Pair Authentication</MenuItem>
                    </TextField>

                    {form.auth_method === 'password' ? (
                      <TextField
                        label="Password *"
                        type="password"
                        placeholder="Enter your password"
                        fullWidth
                        margin="normal"
                        value={form.password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, password: e.target.value }))}
                        required
                        autoComplete="off"
                      />
                    ) : (
                      <>
                        <TextField
                          label="Private Key *"
                          placeholder="-----BEGIN PRIVATE KEY-----
...
-----END PRIVATE KEY-----"
                          fullWidth
                          margin="normal"
                          multiline
                          rows={6}
                          value={form.private_key}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, private_key: e.target.value }))}
                          required
                          helperText="Paste your PEM-formatted private key or provide a file path"
                        />
                        <TextField
                          label="Private Key Passphrase"
                          type="password"
                          placeholder="Optional - if your key is encrypted"
                          fullWidth
                          margin="normal"
                          value={form.private_key_passphrase}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, private_key_passphrase: e.target.value }))}
                        />
                      </>
                    )}
                  </>
                ) : currentConnectorType === 'bigquery' ? (
                  /* BigQuery Configuration */
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>BigQuery Connection</Typography>
                    
                    <TextField
                      label="Service Account JSON *"
                      placeholder='Paste your service account JSON key here'
                      fullWidth
                      margin="normal"
                      multiline
                      rows={8}
                      value={form.service_account_json}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, service_account_json: e.target.value }))}
                      required
                      helperText="Paste the full JSON contents of your Google Cloud service account key file"
                    />
                    <TextField
                      label="Project ID"
                      placeholder="Optional - inferred from service account JSON"
                      fullWidth
                      margin="normal"
                      value={form.project}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, project: e.target.value }))}
                      helperText="Override the project ID from the service account (optional)"
                    />
                  </>
                ) : (
                  /* Postgres/MySQL Configuration */
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Database Connection</Typography>
                    
                    <TextField
                      label="Host *"
                      placeholder="localhost or db.example.com"
                      fullWidth
                      margin="normal"
                      value={form.host}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, host: e.target.value }))}
                      required
                      helperText="Database server hostname or IP address"
                    />
                    <TextField
                      label="Port"
                      type="number"
                      placeholder={currentConnectorType === 'mysql' ? '3306' : currentConnectorType === 'redshift' ? '5439' : '5432'}
                      fullWidth
                      margin="normal"
                      value={form.port}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, port: e.target.value }))}
                    />
                    <TextField
                      label="User *"
                      placeholder="Enter your username"
                      fullWidth
                      margin="normal"
                      value={form.user}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, user: e.target.value }))}
                      required
                      autoComplete="off"
                    />
                    <TextField
                      label="Password *"
                      type="password"
                      placeholder="Enter your password"
                      fullWidth
                      margin="normal"
                      value={form.password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, password: e.target.value }))}
                      required
                      autoComplete="off"
                    />
                    <TextField
                      label="Database *"
                      placeholder="Enter your database name"
                      fullWidth
                      margin="normal"
                      value={form.database}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, database: e.target.value }))}
                      required
                    />
                    {currentConnectorType === 'redshift' && (
                      <TextField
                        label="Schema"
                        placeholder="public"
                        fullWidth
                        margin="normal"
                        value={form.schema}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, schema: e.target.value }))}
                        helperText="Redshift schema to use (defaults to public)"
                      />
                    )}

                    {/* SSH Tunnel Section */}
                    <Divider sx={{ my: 2 }} />
                    <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Checkbox
                            checked={form.ssh_enabled}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, ssh_enabled: e.target.checked }))}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>SSH Tunnel (Optional)</Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Use SSH tunneling to securely connect to databases behind firewalls or in private networks.
                        </Alert>
                        
                        <TextField
                          label="SSH Host *"
                          placeholder="bastion.example.com"
                          fullWidth
                          margin="normal"
                          value={form.ssh_host}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, ssh_host: e.target.value }))}
                          disabled={!form.ssh_enabled}
                          helperText="SSH server hostname or IP"
                        />
                        <TextField
                          label="SSH Port"
                          type="number"
                          placeholder="22"
                          fullWidth
                          margin="normal"
                          value={form.ssh_port}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, ssh_port: e.target.value }))}
                          disabled={!form.ssh_enabled}
                        />
                        <TextField
                          label="SSH User *"
                          placeholder="ubuntu"
                          fullWidth
                          margin="normal"
                          value={form.ssh_user}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, ssh_user: e.target.value }))}
                          disabled={!form.ssh_enabled}
                        />

                        <TextField
                          select
                          label="SSH Authentication"
                          fullWidth
                          margin="normal"
                          value={form.ssh_auth_method}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, ssh_auth_method: e.target.value }))}
                          disabled={!form.ssh_enabled}
                        >
                          <MenuItem value="password">Password</MenuItem>
                          <MenuItem value="key">Private Key</MenuItem>
                        </TextField>

                        {form.ssh_auth_method === 'password' ? (
                          <TextField
                            label="SSH Password *"
                            type="password"
                            placeholder="Enter SSH password"
                            fullWidth
                            margin="normal"
                            value={form.ssh_password}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, ssh_password: e.target.value }))}
                            disabled={!form.ssh_enabled}
                          />
                        ) : (
                          <>
                            <TextField
                              label="SSH Private Key *"
                              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----
...
-----END OPENSSH PRIVATE KEY-----"
                              fullWidth
                              margin="normal"
                              multiline
                              rows={6}
                              value={form.ssh_private_key}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, ssh_private_key: e.target.value }))}
                              disabled={!form.ssh_enabled}
                              helperText="Paste your SSH private key or provide a file path"
                            />
                            <TextField
                              label="SSH Key Passphrase"
                              type="password"
                              placeholder="Optional - if your key is encrypted"
                              fullWidth
                              margin="normal"
                              value={form.ssh_private_key_passphrase}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: typeof form) => ({ ...f, ssh_private_key_passphrase: e.target.value }))}
                              disabled={!form.ssh_enabled}
                            />
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </>
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Stack alignItems="center" spacing={1}>
                    {currentConnectorType === 'mysql' ? <MySQLSvg width={40} height={40} /> : 
                     currentConnectorType === 'snowflake' ? <SnowflakeSvg width={40} height={40} /> : 
                     currentConnectorType === 'redshift' ? <RedshiftSvg width={40} height={40} /> : 
                     currentConnectorType === 'bigquery' ? <BigQuerySvg width={40} height={40} /> : 
                     <PostgresSvg width={40} height={40} />}
                    <Typography variant="h6">
                      {currentConnectorType === 'mysql' ? 'MySQL' : 
                       currentConnectorType === 'snowflake' ? 'Snowflake' : 
                       currentConnectorType === 'redshift' ? 'AWS Redshift' : 
                       currentConnectorType === 'bigquery' ? 'BigQuery' : 
                       'PostgreSQL'}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {currentConnectorType === 'mysql'
                      ? 'Connect to a MySQL database by securely providing your credentials. Once enabled, this connector can be used across interactions.'
                      : currentConnectorType === 'snowflake'
                      ? 'Connect to a Snowflake data warehouse by securely providing your credentials. Once enabled, this connector can be used across interactions.'
                      : currentConnectorType === 'redshift'
                      ? 'Connect to an Amazon Redshift data warehouse by securely providing your credentials. Once enabled, this connector can be used across interactions.'
                      : currentConnectorType === 'bigquery'
                      ? 'Connect to Google BigQuery by providing a service account JSON key. Once enabled, this connector can be used across interactions.'
                      : 'Connect to a PostgreSQL database by securely providing your credentials. Once enabled, this connector can be used across interactions.'}
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <Link href="#" target="_blank" rel="noreferrer">Read the Documentation</Link>
                    <Link href="#" target="_blank" rel="noreferrer">Security & Trust Center</Link>
                  </Stack>
                </Box>
              </Box>
            </Box>
        </AppDialog>
      )}

      {/* Quick Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: (theme: any) => ({
            borderRadius: `${theme.ds.radius.md}px`,
            border: `1px solid ${theme.tokens.border.subtle}`,
            boxShadow: theme.tokens.shadow.lg,
            minWidth: 160,
          })
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={handleToggleActive}
          disabled={togglingActive}
          sx={{ py: 1, px: 1.5, fontSize: '0.8125rem' }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <PowerSettingsNewRounded sx={{ fontSize: 17, color: menuConnector?.status === 'active' ? 'warning.main' : 'success.main' }} />
          </ListItemIcon>
          <ListItemText>{menuConnector?.status === 'active' ? 'Deactivate' : 'Activate'}</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem 
          onClick={() => {
            if (menuConnector) {
              setDeleteDialogOpen(true);
              setConnectorToDelete(menuConnector);
            }
            handleMenuClose();
          }}
          sx={{ 
            py: 1, 
            px: 1.5,
            fontSize: '0.8125rem',
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'error.light',
              color: 'error.contrastText'
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DeleteIcon sx={{ fontSize: 17 }} />
          </ListItemIcon>
          <ListItemText>Permanently Delete</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConnector}
        tone="destructive"
        title="Permanently delete connector?"
        confirmLabel="Permanently delete"
        busy={deleting}
        description={
          <>
            You are about to permanently delete{' '}
            <strong>
              {connectorToDelete?.name || `${connectorToDelete?.type} Connector`}
            </strong>
            . This cannot be undone.
          </>
        }
        body={
          <>
            <Alert severity="error">
              All schemas, profiling data, and configuration for this connector will be deleted forever. Any data models that include this connector may be affected.
            </Alert>
            <Typography variant="caption" color="text.secondary">
              Tip: If you just want to exclude this connector from future sync runs, consider <strong>Deactivating</strong> it instead.
            </Typography>
          </>
        }
      />
    </Box>
  );
}
